import React, { useId, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext.tsx';

export interface HexagonCornerDecorationProps {
    variant?: 'horizontal-degrade' | 'corner' | 'minimal';
    direction?: 'left-to-right' | 'right-to-left';
    corner?: 'bottom-right' | 'top-right' | 'bottom-left' | 'top-left';
    origin?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    minimal?: boolean;
    coverage?: 'full' | 'compact';
    className?: string;
    style?: React.CSSProperties;
    opacity?: number;
}

// Vértices precisos para hexágono regular vertical (pointy-topped)
const getHexPoints = (cx: number, cy: number, r: number): string => {
    const sqrt3_2 = 0.8660254;
    const p0 = `${cx.toFixed(1)},${(cy - r).toFixed(1)}`;
    const p1 = `${(cx + sqrt3_2 * r).toFixed(1)},${(cy - 0.5 * r).toFixed(1)}`;
    const p2 = `${(cx + sqrt3_2 * r).toFixed(1)},${(cy + 0.5 * r).toFixed(1)}`;
    const p3 = `${cx.toFixed(1)},${(cy + r).toFixed(1)}`;
    const p4 = `${(cx - sqrt3_2 * r).toFixed(1)},${(cy + 0.5 * r).toFixed(1)}`;
    const p5 = `${(cx - sqrt3_2 * r).toFixed(1)},${(cy - 0.5 * r).toFixed(1)}`;
    return `${p0} ${p1} ${p2} ${p3} ${p4} ${p5}`;
};

interface DegradeHexItem {
    id: string;
    cx: number;
    cy: number;
    r: number;
    type: 'magenta' | 'purple' | 'gold' | 'cyan' | 'lavender' | 'faint';
    inner: boolean;
    delay: string;
    dur: string;
    fill?: 'magenta' | 'purple' | 'gold';
    isMinimalEssential: boolean; // Para filtrar en modo minimalista
}

const buildHorizontalDegradeHexagons = (R = 40, totalCols = 22, totalRows = 12): DegradeHexItem[] => {
    const W = Math.sqrt(3) * R; // ~69.28px
    const H = 1.5 * R; // 60px
    const items: DegradeHexItem[] = [];

    const pseudo = (row: number, col: number, salt: number) => {
        const x = Math.sin(row * 12.9898 + col * 78.233 + salt * 37.719) * 43758.5453;
        return x - Math.floor(x);
    };

    const originX = -15;
    const originY = 715;

    for (let row = 0; row < totalRows; row++) {
        const cy = 35 + row * H;
        const xShift = (row % 2 === 1) ? (W / 2) : 0;

        // Despejar la zona superior (logo, navbar, encabezados)
        if (cy < 180) continue;

        for (let col = 0; col < totalCols; col++) {
            const cx = 25 + col * W + xShift;
            const dx = cx - originX;
            const dy = originY - cy;
            if (dy < 0) continue;

            // Distancia elíptica ponderada para extenderse hacia la derecha y arriba con caída armónica
            const distEff = Math.hypot(dx * 0.72, dy);

            let keepProb = 0;
            if (distEff < 180) keepProb = 0.65;
            else if (distEff < 340) keepProb = 0.40;
            else if (distEff < 520) keepProb = 0.22;
            else if (distEff < 700) keepProb = 0.12;
            else if (distEff < 880) keepProb = 0.05;
            else keepProb = 0;

            // Anclas clave en la esquina inferior izquierda para enmarcar el inicio
            const isCornerAnchor = (row === 11 && col <= 2) || (row === 10 && col <= 1) || (row === 9 && col === 0);

            const randKeep = pseudo(row, col, 1);
            if (!isCornerAnchor && randKeep > keepProb) continue;

            const randColor = pseudo(row, col, 2);
            let type: DegradeHexItem['type'];
            if (randColor < 0.35) type = 'magenta';
            else if (randColor < 0.60) type = 'purple';
            else if (randColor < 0.78) type = 'gold';
            else if (randColor < 0.88) type = 'cyan';
            else if (randColor < 0.94) type = 'lavender';
            else type = 'faint';

            const randInner = pseudo(row, col, 3);
            const inner = randInner < 0.32 && (type === 'magenta' || type === 'purple' || type === 'gold');

            const randFill = pseudo(row, col, 4);
            let fill: DegradeHexItem['fill'] = undefined;
            if (randFill < 0.14 && distEff < 420) {
                if (type === 'magenta') fill = 'magenta';
                else if (type === 'purple') fill = 'purple';
                else if (type === 'gold') fill = 'gold';
            }

            const delay = `${(pseudo(row, col, 5) * 6).toFixed(1)}s`;
            const dur = `${(4.8 + pseudo(row, col, 6) * 3.4).toFixed(1)}s`;

            // En modo minimalista (Login, Register), conservamos una selección depurada de ~20 hexágonos
            const isMinimalEssential = isCornerAnchor || (pseudo(row, col, 7) > 0.42 && distEff < 600);

            items.push({
                id: `h_${row}_${col}`,
                cx: Math.round(cx * 10) / 10,
                cy: Math.round(cy * 10) / 10,
                r: R,
                type,
                inner,
                delay,
                dur,
                fill,
                isMinimalEssential,
            });
        }
    }

    return items;
};

const STATIC_DEGRADE_HEXAGONS = buildHorizontalDegradeHexagons();

export const HexagonCornerDecoration: React.FC<HexagonCornerDecorationProps> = ({
    variant,
    direction = 'left-to-right',
    corner,
    origin = 'bottom-left',
    minimal = false,
    coverage = 'full',
    className = '',
    style = {},
    opacity = 1,
}) => {
    const { isDark } = useTheme();
    const rawId = useId();
    const id = rawId.replace(/[^a-zA-Z0-9]/g, '_');

    const isMinimal = minimal || variant === 'minimal';
    const isCornerMode = variant === 'corner' || (!variant && !!corner);

    // En modo oscuro eliminamos los filtros blur que producían efecto borroso/sucio
    // y usamos trazos limpios, finos y nítidos con opacidades calibradas
    const getStrokeStyle = (type: string) => {
        const sw = isMinimal ? 1.0 : (isDark ? 1.35 : 1.5);
        const faintSw = isMinimal ? 0.7 : (isDark ? 0.9 : 1.1);

        switch (type) {
            case 'magenta':
                return {
                    stroke: `url(#hexGradMagenta_${id})`,
                    strokeWidth: sw * 1.15,
                    strokeOpacity: isMinimal ? (isDark ? 0.55 : 0.45) : (isDark ? 0.78 : 0.85),
                };
            case 'purple':
                return {
                    stroke: `url(#hexGradPurple_${id})`,
                    strokeWidth: sw * 1.1,
                    strokeOpacity: isMinimal ? (isDark ? 0.48 : 0.40) : (isDark ? 0.72 : 0.80),
                };
            case 'gold':
                return {
                    stroke: `url(#hexGradGold_${id})`,
                    strokeWidth: sw,
                    strokeOpacity: isMinimal ? (isDark ? 0.50 : 0.42) : (isDark ? 0.70 : 0.78),
                };
            case 'cyan':
                return {
                    stroke: `url(#hexGradCyan_${id})`,
                    strokeWidth: sw * 0.95,
                    strokeOpacity: isMinimal ? (isDark ? 0.42 : 0.35) : (isDark ? 0.62 : 0.72),
                };
            case 'lavender':
                return {
                    stroke: isDark ? 'rgba(193, 52, 139, 0.40)' : 'rgba(97, 37, 123, 0.35)',
                    strokeWidth: faintSw * 1.1,
                    strokeOpacity: isMinimal ? 0.28 : (isDark ? 0.45 : 0.55),
                };
            case 'faint':
            default:
                return {
                    stroke: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(97, 37, 123, 0.20)',
                    strokeWidth: faintSw,
                    strokeOpacity: isMinimal ? 0.18 : (isDark ? 0.32 : 0.40),
                };
        }
    };

    const getFill = (fillType?: string) => {
        if (!fillType || isMinimal) return 'transparent';
        if (fillType === 'magenta') return `url(#hexFillMagenta_${id})`;
        if (fillType === 'purple') return `url(#hexFillPurple_${id})`;
        if (fillType === 'gold') return `url(#hexFillGold_${id})`;
        return 'transparent';
    };

    // Gradientes precisos sin blur
    const renderDefs = () => (
        <defs>
            {/* Máscara lineal: inicia en la esquina inferior izquierda y se disuelve suavemente hacia arriba y derecha */}
            <linearGradient
                id={`hexAlphaMaskGrad_${id}`}
                x1={direction === 'right-to-left' ? '100%' : '0%'}
                y1="100%"
                x2={direction === 'right-to-left' ? '25%' : '75%'}
                y2="15%"
            >
                {coverage === 'compact' ? (
                    <>
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                        <stop offset="20%" stopColor="#ffffff" stopOpacity="0.80" />
                        <stop offset="40%" stopColor="#ffffff" stopOpacity="0.45" />
                        <stop offset="65%" stopColor="#ffffff" stopOpacity="0.12" />
                        <stop offset="85%" stopColor="#ffffff" stopOpacity="0" />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                    </>
                ) : (
                    <>
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.98" />
                        <stop offset="25%" stopColor="#ffffff" stopOpacity="0.92" />
                        <stop offset="50%" stopColor="#ffffff" stopOpacity="0.68" />
                        <stop offset="72%" stopColor="#ffffff" stopOpacity="0.32" />
                        <stop offset="88%" stopColor="#ffffff" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                    </>
                )}
            </linearGradient>

            <mask id={`hexDegradeMask_${id}`} maskUnits="userSpaceOnUse" x="0" y="0" width="1600" height="700">
                <rect x="0" y="0" width="1600" height="700" fill={`url(#hexAlphaMaskGrad_${id})`} />
            </mask>

            {/* Gradientes nítidos de alta definición Co Marca */}
            <linearGradient id={`hexGradMagenta_${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={isDark ? '#FF2A7A' : '#C1348B'} />
                <stop offset="60%" stopColor={isDark ? '#C1348B' : '#c1348b'} />
                <stop offset="100%" stopColor="#61257B" />
            </linearGradient>

            <linearGradient id={`hexGradPurple_${id}`} x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#61257B" />
                <stop offset="65%" stopColor={isDark ? '#9B2E9B' : '#8A278A'} />
                <stop offset="100%" stopColor={isDark ? '#C1348B' : '#C1348B'} />
            </linearGradient>

            <linearGradient id={`hexGradGold_${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD97C" />
                <stop offset="60%" stopColor="#EA8406" />
                <stop offset="100%" stopColor="#C1348B" />
            </linearGradient>

            <linearGradient id={`hexGradCyan_${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b3fa8" />
                <stop offset="70%" stopColor="#61257b" />
                <stop offset="100%" stopColor="#61257B" />
            </linearGradient>

            {/* Rellenos translúcidos muy sutiles solo en puntos clave */}
            <radialGradient id={`hexFillMagenta_${id}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={isDark ? 'rgba(193, 52, 139, 0.12)' : 'rgba(193, 52, 139, 0.10)'} />
                <stop offset="100%" stopColor="transparent" />
            </radialGradient>

            <radialGradient id={`hexFillPurple_${id}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={isDark ? 'rgba(97, 37, 123, 0.14)' : 'rgba(97, 37, 123, 0.10)'} />
                <stop offset="100%" stopColor="transparent" />
            </radialGradient>

            <radialGradient id={`hexFillGold_${id}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={isDark ? 'rgba(255, 217, 124, 0.10)' : 'rgba(255, 217, 124, 0.08)'} />
                <stop offset="100%" stopColor="transparent" />
            </radialGradient>
        </defs>
    );

    // MODO ESQUINA (LEGACY)
    if (isCornerMode) {
        const cornerR = 44;
        const cornerHexagons = [
            { id: 'c1', cx: 440, cy: 390, r: cornerR, type: 'magenta' as const, inner: true, delay: '0s', dur: '5.2s' },
            { id: 'c2', cx: 364, cy: 390, r: cornerR, type: 'gold' as const, inner: false, delay: '1.2s', dur: '6.4s' },
            { id: 'c3', cx: 288, cy: 390, r: cornerR, type: 'purple' as const, inner: false, delay: '2.5s', dur: '7.1s' },
            { id: 'c5', cx: 478, cy: 324, r: cornerR, type: 'purple' as const, inner: false, delay: '1.8s', dur: '6.0s' },
            { id: 'c6', cx: 402, cy: 324, r: cornerR, type: 'magenta' as const, inner: true, delay: '0.5s', dur: '4.8s' },
            { id: 'c7', cx: 326, cy: 324, r: cornerR, type: 'gold' as const, inner: true, delay: '2.1s', dur: '6.8s' },
            { id: 'c9', cx: 440, cy: 258, r: cornerR, type: 'magenta' as const, inner: false, delay: '2.8s', dur: '5.6s' },
            { id: 'c10', cx: 364, cy: 258, r: cornerR, type: 'purple' as const, inner: true, delay: '0.9s', dur: '6.5s' },
        ];

        return (
            <div
                className={`hex-corner-decoration-wrap ${className}`}
                style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 'clamp(260px, 30vw, 440px)',
                    height: 'clamp(220px, 26vw, 380px)',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity,
                    ...style,
                }}
            >
                <svg viewBox="0 0 520 440" width="100%" height="100%" preserveAspectRatio="xMaxYMax slice">
                    {renderDefs()}
                    <g>
                        {cornerHexagons.map((hex) => {
                            const strokeStyle = getStrokeStyle(hex.type);
                            const points = getHexPoints(hex.cx, hex.cy, hex.r);
                            const innerPoints = hex.inner ? getHexPoints(hex.cx, hex.cy, hex.r * 0.65) : null;
                            return (
                                <g key={hex.id}>
                                    <polygon
                                        points={points}
                                        fill="transparent"
                                        stroke={strokeStyle.stroke}
                                        strokeWidth={strokeStyle.strokeWidth}
                                        strokeOpacity={strokeStyle.strokeOpacity}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                    {innerPoints && (
                                        <polygon
                                            points={innerPoints}
                                            fill="transparent"
                                            stroke={hex.type === 'magenta' ? `url(#hexGradGold_${id})` : strokeStyle.stroke}
                                            strokeWidth={strokeStyle.strokeWidth * 0.7}
                                            strokeOpacity={strokeStyle.strokeOpacity * 0.85}
                                            strokeLinejoin="round"
                                            strokeLinecap="round"
                                        />
                                    )}
                                </g>
                            );
                        })}
                    </g>
                </svg>
            </div>
        );
    }

    // MODO HORIZONTAL CON DEGRADÉ DE IZQUIERDA A DERECHA
    // Filtramos si está en modo minimalista para un acabado limpio y refinado
    const activeHexagons = isMinimal
        ? STATIC_DEGRADE_HEXAGONS.filter((h) => h.isMinimalEssential)
        : STATIC_DEGRADE_HEXAGONS;

    return (
        <div
            className={`hex-horizontal-degrade-wrap ${className}`}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 1,
                opacity,
                ...style,
            }}
        >
            <style>{`
                @keyframes hexBreathe_${id} {
                    0%, 100% {
                        opacity: 0.88;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.02);
                    }
                }
                @keyframes hexDriftSlow_${id} {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-4px);
                    }
                }
                .hex-wave-layer_${id} {
                    animation: hexDriftSlow_${id} 12s ease-in-out infinite;
                }
            `}</style>

            <svg
                viewBox="0 0 1600 700"
                width="100%"
                height="100%"
                preserveAspectRatio={direction === 'right-to-left' ? 'xMaxYMax slice' : 'xMinYMax slice'}
                style={{
                    transform: direction === 'right-to-left' ? 'scaleX(-1)' : 'none',
                    transformOrigin: 'center',
                }}
            >
                {renderDefs()}

                <g mask={`url(#hexDegradeMask_${id})`} className={`hex-wave-layer_${id}`}>
                    {activeHexagons.map((hex) => {
                        const strokeStyle = getStrokeStyle(hex.type);
                        const fill = getFill(hex.fill);
                        const points = getHexPoints(hex.cx, hex.cy, hex.r);
                        const innerPoints = hex.inner ? getHexPoints(hex.cx, hex.cy, hex.r * 0.65) : null;

                        return (
                            <g
                                key={hex.id}
                                style={{
                                    animation: isMinimal ? 'none' : `hexBreathe_${id} ${hex.dur} ease-in-out infinite`,
                                    animationDelay: hex.delay,
                                    transformOrigin: `${hex.cx}px ${hex.cy}px`,
                                }}
                            >
                                <polygon
                                    points={points}
                                    fill={fill}
                                    stroke={strokeStyle.stroke}
                                    strokeWidth={strokeStyle.strokeWidth}
                                    strokeOpacity={strokeStyle.strokeOpacity}
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                />

                                {innerPoints && (
                                    <polygon
                                        points={innerPoints}
                                        fill="transparent"
                                        stroke={hex.type === 'magenta' ? `url(#hexGradGold_${id})` : strokeStyle.stroke}
                                        strokeWidth={strokeStyle.strokeWidth * 0.7}
                                        strokeOpacity={strokeStyle.strokeOpacity * 0.8}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                )}
                            </g>
                        );
                    })}
                </g>
            </svg>
        </div>
    );
};

export const HexagonDecoration = HexagonCornerDecoration;
export default HexagonCornerDecoration;
